/**
 * Date utilities. All functions are timezone-safe by operating on
 * year/month/day integers extracted via local-time getters.
 *
 * "ISO date" in this module means YYYY-MM-DD (no time component).
 */

/** Format a Date as YYYY-MM-DD (local time). */
export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parse a YYYY-MM-DD string into a local Date at 00:00.
 * Defensive: returns null for non-string, malformed, or NaN dates.
 * Accepts ISO datetimes (e.g. "2026-08-15T00:00:00Z") by parsing the
 * first 10 characters.
 */
export function fromISODate(iso: unknown): Date | null {
  if (typeof iso !== 'string') return null;
  const head = iso.length > 10 ? iso.slice(0, 10) : iso;
  const parts = head.split('-');
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return null;
  }
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return null;
  // Sanity: guard against JS Date rolling over invalid combos (e.g. Feb 30)
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

/** Return a new Date with N days added (does not mutate input). */
export function addDays(date: Date, days: number): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

/** Return a new Date with N months added, clamped to last day of month. */
export function addMonths(date: Date, months: number): Date {
  const d = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(date.getDate(), lastDay));
  return d;
}

/** Whole-day difference between two dates (b - a). */
export function diffDays(a: Date, b: Date): number {
  const ms = startOfDay(b).getTime() - startOfDay(a).getTime();
  return Math.round(ms / 86_400_000);
}

/** Return midnight of the given date (local time). */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Today's date at 00:00 local time. */
export function today(): Date {
  return startOfDay(new Date());
}

/** True if a and b are the same calendar day. */
export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** True if `date` is strictly before `other`. */
export function isBefore(date: Date, other: Date): boolean {
  return startOfDay(date).getTime() < startOfDay(other).getTime();
}

/** True if `date` is strictly after `other`. */
export function isAfter(date: Date, other: Date): boolean {
  return startOfDay(date).getTime() > startOfDay(other).getTime();
}

/** Inclusive on both ends. */
export function isWithin(date: Date, start: Date, end: Date): boolean {
  const t = startOfDay(date).getTime();
  return t >= startOfDay(start).getTime() && t <= startOfDay(end).getTime();
}

/** Format a date for human display, e.g. "Sat, 7 Jun 2025". */
export function formatHumanDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Format a date as "7 June 2025". */
export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Month label e.g. "June 2025". */
export function formatMonthLabel(date: Date): string {
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/**
 * Day index in the Mon=0 ... Sun=6 system.
 * JavaScript's getDay() returns Sun=0 ... Sat=6 which we shift.
 */
export function mondayBasedDayIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/**
 * Build a 6×7 matrix of dates for the calendar grid of `monthAnchor`'s month.
 * Leading/trailing days from neighbouring months are included so the grid
 * always has 42 cells. Mon-first.
 */
export function buildMonthMatrix(monthAnchor: Date): Date[] {
  const firstOfMonth = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
  const leading = mondayBasedDayIndex(firstOfMonth);
  const gridStart = addDays(firstOfMonth, -leading);
  const out: Date[] = [];
  for (let i = 0; i < 42; i++) {
    out.push(addDays(gridStart, i));
  }
  return out;
}

/** True if `date` belongs to the same month/year as `monthAnchor`. */
export function isSameMonth(date: Date, monthAnchor: Date): boolean {
  return (
    date.getFullYear() === monthAnchor.getFullYear() &&
    date.getMonth() === monthAnchor.getMonth()
  );
}

/**
 * True if any day in [start, end) (exclusive end) intersects a blocked range
 * [blockStart, blockEnd] (inclusive on both ends).
 */
export function rangeIntersectsBlocked(
  start: Date,
  endExclusive: Date,
  blockStart: Date,
  blockEnd: Date,
): boolean {
  // Walk day-by-day; ranges are small (max stays).
  const lastNight = addDays(endExclusive, -1);
  if (isBefore(lastNight, blockStart)) return false;
  if (isAfter(start, blockEnd)) return false;
  return true;
}
